import { useEffect, useState } from 'react';
import useClient from 'api/client';
import listEnvironmentLabels from 'api/Organization/listEnvironmentLabels';
import { EnvironmentListItem, EnvironmentSelectItem } from 'types/Environment';
import listContextGroups from 'api/Environment/listContextGroups';
import getEnvironmentStatus from 'api/Environment/getEnvironmentStatus';
import { SearchFilter } from 'types/Filters';

export default function useListEnvironments(
    environmentUri: string,
    filter: SearchFilter,
) {
    const client = useClient() as unknown as {
        query: (query: any) => Promise<any>;
    };
    const [roles, setRoles] = useState<string[]>([]);
    const [loading, setLoading] = useState(true);
    const [environments, setItems] = useState<EnvironmentSelectItem[]>([]);
    const [environmentError, setEnvironmentError] = useState('');
    const [rolesError, setRolesError] = useState('');

    useEffect(() => {
        const fetchData = async () => {
            try {
                setRoles([]);
                setLoading(true);
                let allEnvironments: EnvironmentSelectItem[] = [];
                let currentPage = 1;
                let totalPages = 1;

                do {
                    const response = await client?.query(
                        listEnvironmentLabels({ ...filter, page: currentPage }),
                    );

                    if (!response.errors) {
                        const data = response.data.listEnvironments;
                        totalPages = data.pages;

                        allEnvironments = [
                            ...allEnvironments,
                            ...data.nodes.map((e: EnvironmentListItem) => ({
                                label: e.label,
                                value: e.environmentUri,
                                key: e.environmentUri,
                                organizationUri: e.organization.organizationUri,
                            })),
                        ];
                        allEnvironments = [
                            ...new Set(
                                allEnvironments.map((env) =>
                                    JSON.stringify(env),
                                ),
                            ),
                        ].map((env) => JSON.parse(env));

                        setEnvironmentError('');
                    } else {
                        setEnvironmentError('Could not fetch environments');
                        break;
                    }

                    currentPage += 1;
                } while (currentPage <= totalPages);

                setItems(allEnvironments);
            } finally {
                setLoading(false);
            }
        };

        if (client) {
            fetchData();
        }
    }, [client]);

    useEffect(() => {
        const fetchRoles = async () => {
            try {
                const response = await client?.query(
                    listContextGroups({
                        environmentUri,
                        filter: { samlgroup: ['admin'] },
                        normalizeGroups: false,
                    }),
                );
                if (!response.errors) {
                    setRoles(
                        response.data.listContextGroups.map(
                            (value: string) => ({
                                value: value,
                                label: value,
                            }),
                        ),
                    );
                } else {
                    setRolesError('Could not fetch roles');
                }
            } finally {
                setLoading(false);
            }
        };

        const fetchEnvironmentStatus = async () => {
            try {
                const response = await client?.query(
                    getEnvironmentStatus(environmentUri),
                );
                if (
                    response.data.getEnvironment.stack.status ===
                    'CREATE_FAILED'
                ) {
                    setEnvironmentError(
                        'This environment has failed to create',
                    );
                } else {
                    setEnvironmentError('');
                }
            } finally {
                setLoading(false);
            }
        };
        if (client && environmentUri !== '') {
            fetchRoles();
            fetchEnvironmentStatus();
        }
    }, [client, environmentUri]);

    return { environments, environmentError, rolesError, loading, roles };
}
